import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Image, useMantineTheme } from "@mantine/core";
import { logger } from "utils/logger";
import styles from "./LiveFeedNav.module.scss";

const VerticalGenieNavigation = ({
  assets,
  currentIndex,
  onIndexChange,
  forwardedRef,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(currentIndex ?? 0);
  const [scrollOffset, setScrollOffset] = useState(0);
  const containerRef = useRef(null);
  const accumulatedDeltaRef = useRef(0);
  const lastScrollTimeRef = useRef(0);
  const itemHeight = 70; // Height of each navigation item in pixels
  const theme = useMantineTheme();
  // State to track which items we should render
  const [visibleRange, setVisibleRange] = useState({ start: 0, end: 0 });
  // Function to determine which items should be rendered
  const updateVisibleItems = useCallback((index) => {
    if (!containerRef.current || !assets) return;

    const containerHeight = containerRef.current.clientHeight;

    // Calculate how many items would be visible above and below the selected item
    // Adding extra buffer for smooth scrolling and animations
    const buffer = Math.ceil(containerHeight / itemHeight) + 5;

    // Calculate start and end indices with buffer
    const start = Math.max(0, index - buffer);
    const end = Math.min(assets ? assets.length - 1 : 0, index + buffer);

    setVisibleRange({ start, end });
  },[setVisibleRange, assets]);

  // Handle wheel events to scroll through items with proportional speed
  const handleWheel = useCallback(
    (deltaY) => {
      // e.preventDefault();

      if (!assets || assets.length === 0) return;

      const now = Date.now();
      const deltaTime = now - lastScrollTimeRef.current;
      lastScrollTimeRef.current = now;

      // Reset accumulated delta if there's a pause in scrolling
      if (deltaTime > 200) {
        accumulatedDeltaRef.current = 0;
      }

      // Calculate scroll sensitivity based on delta time and delta magnitude
      let sensitivity = 0.005;

      // Add the current wheel delta to the accumulated delta
      accumulatedDeltaRef.current += deltaY * sensitivity;

      // Determine if we've accumulated enough to change the index
      if (Math.abs(accumulatedDeltaRef.current) >= 1) {
        // Get the integer part of the accumulated delta
        const indexChange = Math.floor(Math.abs(accumulatedDeltaRef.current));
        const direction = accumulatedDeltaRef.current > 0 ? 1 : -1;

        // Calculate new index with bounds checking
        const newIndex = Math.min(
          Math.max(0, selectedIndex + indexChange * direction),
          assets.length - 1
        );

        // Only update if the index has changed

        if (newIndex !== selectedIndex) {
          setSelectedIndex(newIndex);
          updateScrollOffset(newIndex);
          updateVisibleItems(newIndex);
          logger.log("SETTING SELECTED INDEX: ", newIndex, selectedIndex);
          onIndexChange(newIndex,selectedIndex);
        }

        // Subtract the used portion of the delta
        accumulatedDeltaRef.current -= indexChange * direction;
      }
    },
    [
      assets,
      selectedIndex,
      setSelectedIndex,
      updateVisibleItems,
      onIndexChange
    ]
  );

  useEffect(() => {
    // Ensure initial selected item is centered
    setTimeout(() => {
      updateScrollOffset(selectedIndex);
      updateVisibleItems(selectedIndex);
    }, 100);

    // Update offset if window is resized
    const handleResize = () => {
      updateScrollOffset(selectedIndex);
      updateVisibleItems(selectedIndex);
    };

    window.addEventListener("resize", handleResize);

    // if (containerRef.current) {
    //   containerRef.current.addEventListener('wheel', handleWheel, { passive: false });
    // }
    const currentContainer = containerRef.current;

    return () => {
      window.removeEventListener("resize", handleResize);
      if (currentContainer)
        currentContainer.removeEventListener("wheel", handleWheel);
    };

  }, [handleWheel, updateVisibleItems, selectedIndex]);

  // Update visible items whenever assets or selectedIndex changes
  useEffect(() => {
    updateVisibleItems(selectedIndex);
  }, [assets,selectedIndex,updateVisibleItems]);

  // Update scroll offset to keep selected item centered
  const updateScrollOffset = (index) => {
    if (!containerRef.current) return;

    const exactCenterPosition = index * itemHeight;
    const containerHeight = containerRef.current.clientHeight;
    const halfContainerHeight = containerHeight / 2;

    // Calculate precise offset to center the item
    const preciseOffset =
      exactCenterPosition - halfContainerHeight + itemHeight / 2;
    setScrollOffset(preciseOffset);
  };

  // Handle direct item click
  const handleItemClick = (index) => {
    logger.log("CHANGE : ", index)
    setSelectedIndex(index);
    updateScrollOffset(index);
    updateVisibleItems(index);
    onIndexChange(index,currentIndex);
  };

  // useEffect(() => {
  //   if(currentIndex !== selectedIndex){
  //     onIndexChange(selectedIndex,currentIndex);
  //   }
  // }, [selectedIndex,currentIndex,onIndexChange]);

  // Calculate dynamic properties for each item based on its position
  const getItemVariants = (index) => {
    const isSelected = index === selectedIndex;
    const distanceFromCenter = Math.abs(index - selectedIndex);

    // Calculate properties with smoother falloff
    // const opacity = Math.max(0.3, 1 - distanceFromCenter * 0.12);
    const scale = isSelected ? 1 : Math.max(0.8, 1 - distanceFromCenter * 0.2);
    const blur =
      distanceFromCenter > 4
        ? Math.min(1.5, (distanceFromCenter - 4) * 0.3)
        : 0;

    return {
      selected: {
        opacity: 1,
        scale: 1,
        // filter: "blur(0px)",
        // backgroundColor: "rgba(245, 245, 250, 1)",
        fontWeight: 600,
        borderColor: theme.colors.brand[4],
        y: 0,
      },
      notSelected: {
        opacity: 1,
        scale,
        // filter: `blur(${blur}px)`,
        backgroundColor: "rgba(255, 255, 255, 0)",
        fontWeight: 400,
        y: 0,
      },
    };
  };

  // Expose the handleWheel method via ref for external access
  useEffect(() => {
    if (forwardedRef) {
      forwardedRef.current = {
        handleWheel,
      };
    }
  }, [forwardedRef, handleWheel]);

  const centerIndicatorStyle = {
    height: itemHeight + "px",
  };

  const itemWrapperStyle = {
    // position: 'absolute',
    // width: '100%',
    height: (assets?.length || 0) * itemHeight + "px", // Total height of all items
  };

  const itemStyle = {
    height: itemHeight + "px",
    left: 0,
  };

  // const debugCenterStyle = {
  //   position: 'absolute',
  //   top: '50%',
  //   left: 0,
  //   width: '100%',
  //   height: '1px',
  //   backgroundColor: 'rgba(255, 0, 0, 0.3)',
  //   zIndex: 20,
  //   transform: 'translateY(-0.5px)',
  //   pointerEvents: 'none'
  // };

  // Get only the visible items for rendering
  const getVisibleItems = () => {
    if (!assets) return [];

    return assets
      .slice(visibleRange.start, visibleRange.end + 1)
      .map((item, i) => {
        const actualIndex = i + visibleRange.start;
        return { ...item, actualIndex };
      });
  };

  const visibleItems = getVisibleItems();

  return (
    <div className={styles.container}>
      <div
        ref={containerRef}
        className={styles.paper}
        onWheel={(e) => handleWheel(e.deltaY)}
      >
        {/* Center indicator */}
        <div className={styles.centerIndicator} style={centerIndicatorStyle} />

        {/* Debug center line (can be removed in production) */}
        <div className={styles.debugCenterStyle} />

        {/* Items container with Framer Motion */}
        <motion.div
          className={styles.itemWrapper}
          style={itemWrapperStyle}
          initial={false}
          // animate={{ y: -scrollOffset }}
          animate={{ y: scrollOffset > 0 ? -scrollOffset : 0 }}
          transition={{
            type: "spring",
            stiffness: 150,
            damping: 20,
            mass: 0.5,
          }}
        >
          <AnimatePresence>
            {visibleItems.map((item) => {
              const index = item.actualIndex;
              const variants = getItemVariants(index);

              return (
                <motion.div
                  key={index}
                  className={styles.item}
                  style={{
                    ...itemStyle,
                    height: itemHeight,
                    top: index * itemHeight + "px",
                  }}
                  variants={variants}
                  initial="notSelected"
                  animate={index === selectedIndex ? "selected" : "notSelected"}
                  transition={{
                    type: "spring",
                    stiffness: 400,
                    damping: 30,
                    mass: 0.8,
                    // stiffness: 400,
                    // damping: 30,
                    // mass: 0.8
                  }}
                  onClick={() => handleItemClick(index)}
                  whileHover={{ scale: index === selectedIndex ? 1.02 : 0.92 }}
                  whileTap={{ scale: 0.98 }}
                >
                  <Image
                    width={"100%"}
                    height={itemHeight - 10}
                    src={item.thumb}
                    fit="cover"
                    style={{ borderRadius: theme.radius.md, flex:1 }}
                  />
                </motion.div>
              );
            })}
          </AnimatePresence>
        </motion.div>
      </div>
    </div>
  );
};

// export default VerticalGenieNavigation;

// Create a forwardRef version of the component
const ForwardedVerticalGenieNavigation = React.forwardRef((props, ref) => {
  return <VerticalGenieNavigation {...props} forwardedRef={ref} />;
});

export default ForwardedVerticalGenieNavigation;
